import { CADDrawingUnitHelper } from "./CADDrawing/CADDrawingUnitHelper";
import { EdgeAdjustment } from "./Components/EdgeTools/AddEdgeAdjustment";
import { CADOperationTypeEnum, EdgeAdjustmentsEnum, SegmentTypeEnum } from "./enums";
import { GeometryHelpers, VectorHelpers } from "./GeometryHelpers";
import { IArc, ICADLayer, ICADOperation, ICircle, ILine, IPoint, ISegment } from "./interfaces";

export class CADHitTesting {

    public static HitTestEdgeAdjustments(layer: ICADLayer, modelPos: IPoint, unitHelper: CADDrawingUnitHelper): EdgeAdjustment | null {

        const widthInModelUnits = unitHelper.ConvertCanvasDistanceToModelSpace(15); //15px

        const adjustments: EdgeAdjustment[] = [];

        adjustments.push(...layer.edgeElbows.map(elbow => ({ ...elbow, adjustmentType: EdgeAdjustmentsEnum.Elbow })));
        adjustments.push(...layer.edgeRakes.map(rake => ({ ...rake, adjustmentType: EdgeAdjustmentsEnum.Rake })));
        adjustments.push(...layer.edgeShifts.map(shift => ({ ...shift, adjustmentType: EdgeAdjustmentsEnum.Shift })));

        for (const adjustment of adjustments) {
            //Get edge for this adjustment
            const edge = layer.edges.find(e => e.edgeName === adjustment.edgeName);
            if (edge) {
                if (this.HitTestSegments(edge.edgeSegments, modelPos, widthInModelUnits)) {
                    return adjustment;
                }
            }
        }

        return null;

    }

    public static HitTestOperation(layer: ICADLayer, operations: ICADOperation[], modelPos: IPoint,  unitHelper:CADDrawingUnitHelper): ICADOperation | null {

        const widthInModelUnits = unitHelper.ConvertCanvasDistanceToModelSpace(15); //15px

        for (const op of operations) {

            switch (op.operationType) {
                case CADOperationTypeEnum.EdgeProcessing:
                    const edgeworkEdges: ISegment[] = this.EdgeworkOperationEdgeSegments(layer, op);
                    if (this.HitTestSegments(edgeworkEdges, modelPos, widthInModelUnits)) {
                        return op;
                    }
                    break;

                case CADOperationTypeEnum.InternalCutout:
                case CADOperationTypeEnum.DrillHole:
                    //Match anywhere inside the region
                    for (const region of op.regions) {
                        if (GeometryHelpers.PointIsInsideShape(modelPos, region)) {
                            return op;
                        }
                    }
                    break;

                default:
                    //Everything else we should hit test the individual segments
                    for (const region of op.regions) {
                        if (this.HitTestSegments(region, modelPos, widthInModelUnits)) {
                            return op;
                        }
                    }

            }

        }

        return null;
    }


    private static EdgeworkOperationEdgeSegments(layer: ICADLayer, op: ICADOperation): ISegment[] {
        const edgeworkEdges: ISegment[] = [];
        if (op.edgeName === "0" || op.edgeName === "") {
            for (const edge of layer.edges) {
                edgeworkEdges.push(...edge.edgeSegments);
            }
        }
        else {
            const edge = layer.edges.find(e => e.edgeName === op.edgeName);
            if (edge) {
                edgeworkEdges.push(...edge.edgeSegments);
            }
        }
        return edgeworkEdges;
    }

    private static HitTestSegments(segments: ISegment[], modelPos: IPoint, width: number): boolean {

        for (const segment of segments) {
            switch (segment.type) {
                case SegmentTypeEnum.Line:

                    if (this.HitTestLineSegment(modelPos, segment as ILine, width)) {
                        return true;
                    }

                    break;

                case SegmentTypeEnum.Arc:

                    if (this.HitTestArcSegment(modelPos, segment as IArc, width)) {
                        return true;
                    }

                    break;

                case SegmentTypeEnum.Circle:

                    if (this.HitTestCircleSegment(modelPos, segment as ICircle, width)) {
                        return true;
                    }
                    break;

            }
        }

        return false;

    }

    private static HitTestLineSegment(modelPos: IPoint, line: ILine, width: number): boolean {
        const startPoint = line.startPoint;
        const endPoint = line.endPoint;

        const vector = VectorHelpers.FromPoints(startPoint, endPoint);
        const perpendicularVector = VectorHelpers.GetPerpendicularVector(vector);
        const normalizedPerpendicular = VectorHelpers.NormalizeVector(perpendicularVector);
        const scaledPerpendicular = VectorHelpers.Scale(normalizedPerpendicular, width / 2);

        // Create the hit rectangle around the line segment
        const hitRect: ISegment[] = [
            { startPoint: VectorHelpers.Subtract(startPoint, scaledPerpendicular), endPoint: VectorHelpers.Subtract(endPoint, scaledPerpendicular), type: SegmentTypeEnum.Line, side: 0 },
            { startPoint: VectorHelpers.Subtract(endPoint, scaledPerpendicular), endPoint: VectorHelpers.Add(endPoint, scaledPerpendicular), type: SegmentTypeEnum.Line, side: 1 },
            { startPoint: VectorHelpers.Add(endPoint, scaledPerpendicular), endPoint: VectorHelpers.Add(startPoint, scaledPerpendicular), type: SegmentTypeEnum.Line, side: 2 },
            { startPoint: VectorHelpers.Add(startPoint, scaledPerpendicular), endPoint: VectorHelpers.Subtract(startPoint, scaledPerpendicular), type: SegmentTypeEnum.Line, side: 3 }
        ];

        return GeometryHelpers.PointIsInsideShape(modelPos, hitRect);
    }

    private static HitTestArcSegment(modelPos: IPoint, arc: IArc, width: number): boolean {
        const ac1: ICircle = { centerPoint: arc.centerPoint, radius: arc.radius + width / 2, type: SegmentTypeEnum.Circle, startPoint: arc.startPoint, endPoint: arc.endPoint, side: 0 };
        const ac2: ICircle = { centerPoint: arc.centerPoint, radius: arc.radius - width / 2, type: SegmentTypeEnum.Circle, startPoint: arc.startPoint, endPoint: arc.endPoint, side: 1 };

        //Check if is bounded by the two cicles and is in the portion of the circle that defines this arc
        if (GeometryHelpers.PointIsInsideShape(modelPos, [ac1]) &&
            !GeometryHelpers.PointIsInsideShape(modelPos, [ac2]) &&
            !GeometryHelpers.PointIsLeftOfLine(modelPos, arc.startPoint, arc.endPoint)) {
            return true;
        }

        return false;
    }

    private static HitTestCircleSegment(modelPos: IPoint, circle: ICircle, width: number): boolean {
        const c1: ICircle = { centerPoint: circle.centerPoint, radius: circle.radius + width / 2, type: SegmentTypeEnum.Circle, startPoint: circle.startPoint, endPoint: circle.endPoint, side: 0 };
        const c2: ICircle = { centerPoint: circle.centerPoint, radius: circle.radius - width / 2, type: SegmentTypeEnum.Circle, startPoint: circle.startPoint, endPoint: circle.endPoint, side: 1 };

        //Check if is bounded by the two cicles
        if (GeometryHelpers.PointIsInsideShape(modelPos, [c1]) && !GeometryHelpers.PointIsInsideShape(modelPos, [c2])) {
            return true;
        }

        return false;
    }
}
